

import { useState, useEffect, useMemo } from "react";
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  CreditCard,
} from "lucide-react";
import DashboardLayout from "../components/DashboardLayout";
import Modal from "../components/Modal";
import DeleteConfirmationModal from "../components/DeleteConfirmationModal";
import { mastersApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { withAuth } from "@/app/components/withAuth";

interface PaymentMethod {
  id: number;
  name: string;
  status: string;
  created_at?: string;
}

// Reusable Action Button Component
function ActionButton({
  onClick,
  icon: Icon,
  label,
  variant = "default",
}: {
  onClick: () => void;
  icon: React.ElementType;
  label: string;
  variant?: "default" | "primary" | "danger";
}) {
  const colors = {
    default: "text-stone-600 hover:bg-stone-100",
    primary: "text-amber-600 hover:bg-amber-50",
    danger: "text-red-600 hover:bg-red-50",
  };
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium transition-colors ${colors[variant]}`}
      title={label}
    >
      <Icon className="w-4 h-4" />
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}

// Reusable Payment Method Form Component
function PaymentMethodForm({
  initialData,
  onSubmit,
  onCancel,
}: {
  initialData?: Partial<PaymentMethod>;
  onSubmit: (data: { name: string; status: string }) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initialData?.name || "");
  const [status, setStatus] = useState(initialData?.status || "active");

  // Reset form when initialData changes (for new entries)
  useEffect(() => {
    if (!initialData?.id) {
      setName("");
      setStatus("active");
    } else {
      setName(initialData.name || "");
      setStatus(initialData.status || "active");
    }
  }, [initialData]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({ name, status });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-xs font-medium text-stone-500 mb-1.5">Payment Method Name *</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., Cash, UPI, Bank Transfer"
          className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
          required
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-stone-500 mb-1.5">Status</label>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="w-full px-3 py-2.5 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
        >
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>
      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-4 py-2.5 rounded-xl bg-stone-100 text-stone-600 font-semibold text-sm hover:bg-stone-200 transition-colors border border-stone-200"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="flex-1 px-4 py-2.5 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90 transition-opacity"
        >
          {initialData?.id ? "Update" : "Add"} Payment Method
        </button>
      </div>
    </form>
  );
}


function PaymentMethodsPage() {
  const { showToast } = useToast();

  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMethod, setEditingMethod] = useState<PaymentMethod | null>(null);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingMethod, setDeletingMethod] = useState<PaymentMethod | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await mastersApi.paymentMethods.list();
      setPaymentMethods(response.data?.data || response.data || []);
    } catch (error) {
      showToast("Failed to load payment methods", "error");
    } finally {
      setLoading(false);
    }
  };

  const filteredMethods = useMemo(() => {
    if (!searchQuery) return paymentMethods;
    const q = searchQuery.toLowerCase();
    return paymentMethods.filter((m) => m.name.toLowerCase().includes(q));
  }, [paymentMethods, searchQuery]);

  const handleDelete = async () => {
    if (!deletingMethod) return;
    setDeleteLoading(true);
    
    try {
      await mastersApi.paymentMethods.delete(deletingMethod.id);
      showToast("Payment method deleted successfully", "success");
      setDeleteModalOpen(false);
      setDeletingMethod(null);
      loadData(); // Reload data to ensure consistency
    } catch (error: unknown) {
      // Show dependency error message if deletion failed due to dependencies
      const errorMessage = error instanceof Error ? error.message : "Failed to delete payment method";
      showToast(errorMessage, "error");
      setDeleteModalOpen(false);
      setDeletingMethod(null);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleFormSubmit = async (data: { name: string; status: string }) => {
    try {
      if (editingMethod) {
        // Update existing
        const updated = { ...editingMethod, ...data };
        setPaymentMethods((p) => p.map((m) => (m.id === editingMethod.id ? (updated as PaymentMethod) : m)));
        await mastersApi.paymentMethods.update(editingMethod.id, data);
        showToast("Payment method updated successfully", "success");
      } else {
        // Create new
        const tempId = Date.now();
        setPaymentMethods((p) => [{ id: tempId, ...data } as PaymentMethod, ...p]);
        const response = await mastersApi.paymentMethods.create(data);
        const newMethod = response.data?.data || response.data;
        setPaymentMethods((p) => p.map((m) => (m.id === tempId ? newMethod : m)));
        showToast("Payment method created successfully", "success");
      }
      
      // Close modal and reset form
      setIsModalOpen(false);
      setEditingMethod(null);
    } catch (error) {
      showToast("Failed to save payment method", "error");
      // Reload data on error to sync state
      loadData();
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-amber-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-stone-800 mb-1">Payment Methods</h1>
        <p className="text-sm text-stone-500">Manage payment methods used across vendor and client payments</p>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search payment methods..."
            className="w-full pl-9 pr-4 py-2 rounded-xl border border-stone-200 text-sm focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
          />
        </div>
        <button
          onClick={() => { 
            setEditingMethod(null); 
            setIsModalOpen(true); 
          }}
          className="px-4 py-2 min-h-10 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Method
        </button>
      </div>

      {/* Cards on mobile, table from md up */}
      {filteredMethods.length === 0 ? (
        <div className="text-center py-12 bg-stone-50 rounded-xl border border-stone-200">
          <CreditCard className="w-12 h-12 text-stone-300 mx-auto mb-3" />
          <p className="text-stone-500">No payment methods found</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          {/* Mobile cards */}
          <div className="md:hidden divide-y divide-stone-100">
            {filteredMethods.map((method) => (
              <div key={method.id} className="p-4 flex items-center justify-between gap-3">
                <div className="min-w-0 flex items-center gap-2">
                  <p className="font-semibold text-stone-800 truncate">{method.name}</p>
                  <span
                    className={`shrink-0 text-xs px-2 py-1 rounded-lg font-medium ${
                      method.status === "active"
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-stone-100 text-stone-500"
                    }`}
                  >
                    {method.status}
                  </span>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <button
                    onClick={() => {
                      setEditingMethod(method);
                      setIsModalOpen(true);
                    }}
                    className="h-10 w-10 rounded-lg bg-stone-100 text-stone-600 hover:bg-stone-200 flex items-center justify-center"
                    aria-label={`Edit ${method.name}`}
                    title="Edit"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => {
                      setDeletingMethod(method);
                      setDeleteModalOpen(true);
                    }}
                    className="h-10 w-10 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 flex items-center justify-center"
                    aria-label={`Delete ${method.name}`}
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-stone-50">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500">Name</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-stone-500">Status</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-stone-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {filteredMethods.map((method) => (
                  <tr key={method.id} className="hover:bg-stone-50">
                    <td className="px-4 py-3 font-medium text-stone-800">{method.name}</td>
                    <td className="px-4 py-3 text-center">
                      <span
                        className={`text-xs px-2 py-1 rounded-lg font-medium ${
                          method.status === "active"
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-stone-100 text-stone-500"
                        }`}
                      >
                        {method.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <ActionButton 
                          onClick={() => { 
                            setEditingMethod(method); 
                            setIsModalOpen(true); 
                          }} 
                          icon={Pencil} 
                          label="Edit" 
                        />
                        <ActionButton 
                          onClick={() => { 
                            setDeletingMethod(method); 
                            setDeleteModalOpen(true); 
                          }} 
                          icon={Trash2} 
                          label="Delete" 
                          variant="danger" 
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Edit/Create Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => { 
          setIsModalOpen(false); 
          setEditingMethod(null); 
        }}
        title={editingMethod ? "Edit Payment Method" : "Add New Payment Method"}
        size="md"
      >
        <PaymentMethodForm
          initialData={editingMethod || undefined}
          onSubmit={handleFormSubmit}
          onCancel={() => { 
            setIsModalOpen(false); 
            setEditingMethod(null); 
          }}
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => { 
          setDeleteModalOpen(false); 
          setDeletingMethod(null); 
        }}
        onConfirm={handleDelete}
        title="Delete Payment Method"
        message="Are you sure you want to delete this payment method? This action cannot be undone."
        itemName={deletingMethod?.name}
        loading={deleteLoading}
      />
    </DashboardLayout>
  );
}

export default withAuth(PaymentMethodsPage, { allowedRoles: ['admin', 'super_admin', 'manager'] });
